import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminDepositAPI, adminWithdrawalAPI } from '@/lib/api';
import { ArrowDownToLine, ArrowUpFromLine, Check, X, Eye, Image, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
const statusColor = { pending: 'text-amber-400 bg-amber-500/15', approved: 'text-emerald-400 bg-emerald-500/15', rejected: 'text-red-400 bg-red-500/15' };

export default function AdminDepositsWithdrawalsPage() {
  const [tab, setTab] = useState('deposits');
  const [depFilter, setDepFilter] = useState('pending');
  const [wdFilter, setWdFilter] = useState('pending');
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [screenshotUrl, setScreenshotUrl] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [deps, wds] = await Promise.all([
        adminDepositAPI.getAll(depFilter), adminWithdrawalAPI.getAll(wdFilter)
      ]);
      setDeposits(deps.data);
      setWithdrawals(wds.data);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [depFilter, wdFilter]); // eslint-disable-line

  const reviewDeposit = async (id, action) => {
    try {
      await adminDepositAPI.review(id, action);
      toast.success(`Deposit ${action}d`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const reviewWithdrawal = async (id, action) => {
    try {
      await adminWithdrawalAPI.review(id, action);
      toast.success(`Withdrawal ${action}d`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-deposits-withdrawals">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
               <div>
                 <h1 className="text-xl font-bold font-['Outfit'] text-[var(--text-primary)]">Payment Management</h1>
                 <p className="text-sm text-[var(--text-secondary)] mt-1">Approve or reject user transactions</p>
               </div>
               <button onClick={load} data-testid="refresh-payments-btn" className="flex items-center gap-1.5 px-4 py-2 bg-green-500/15 text-green-400 rounded-lg text-xs font-medium hover:bg-green-500/25 transition-colors border border-green-500/20">
                 <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
               </button>
             </div>

        <div className="flex gap-2">
          {[{ key: 'deposits', label: 'Deposits', icon: ArrowDownToLine }, { key: 'withdrawals', label: 'Withdrawals', icon: ArrowUpFromLine }].map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`dw-tab-${t.key}`}
                className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-all ${tab === t.key ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] border border-[var(--border-subtle)]'}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Deposits Tab */}
        {tab === 'deposits' && (
          <div className="space-y-4">
            <div className="flex gap-2">
              {['pending', 'approved', 'rejected', 'all'].map(f => (
                <button key={f} onClick={() => setDepFilter(f)} data-testid={`dep-filter-${f}`}
                  className={`px-3 py-1.5 rounded text-xs font-medium ${depFilter === f ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] border border-[var(--border-subtle)]'}`}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded overflow-x-auto">
              {deposits.length === 0 ? (
                <div className="p-8 text-center text-sm text-[var(--text-secondary)]">No {depFilter} deposits</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-[var(--text-secondary)] uppercase border-b border-[var(--border-subtle)]">
                      <th className="text-left px-4 py-3">User</th>
                      <th className="text-right px-3 py-3">Amount (INR)</th>
                      <th className="text-right px-3 py-3">Amount (USD)</th>
                      <th className="text-left px-3 py-3">Mode</th>
                      <th className="text-left px-3 py-3">TX ID</th>
                      <th className="text-center px-3 py-3">Screenshot</th>
                      <th className="text-left px-3 py-3">Date</th>
                      <th className="text-left px-3 py-3">Status</th>
                      <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deposits.map((d) => (
                      <tr key={d.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)]">
                        <td className="px-4 py-3">
                          <p className="text-xs font-medium text-[var(--text-primary)]">{d.user_name}</p>
                          <p className="text-[10px] text-[var(--text-tertiary)]">{d.user_email}</p>
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-[var(--text-primary)]">₹{d.amount_inr?.toLocaleString()}</td>
                        <td className="px-3 py-3 text-right font-mono text-emerald-400">{formatCurrency(d.amount_usd)}</td>
                        <td className="px-3 py-3 text-xs text-[var(--text-secondary)]">{d.payment_mode}</td>
                        <td className="px-3 py-3 font-mono text-xs text-[var(--text-secondary)]">{d.transaction_id}</td>
                        <td className="px-3 py-3 text-center">
                          {d.screenshot_url ? (
                            <button onClick={() => setScreenshotUrl(API_BASE + d.screenshot_url)} className="p-1 text-blue-400 hover:text-blue-300">
                              <Image className="w-4 h-4" />
                            </button>
                          ) : <span className="text-[10px] text-[var(--text-tertiary)]">None</span>}
                        </td>
                        <td className="px-3 py-3 text-xs text-[var(--text-secondary)]">{new Date(d.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</td>
                        <td className="px-3 py-3"><span className={`px-2 py-0.5 rounded text-xs ${statusColor[d.status] || ''}`}>{d.status}</span></td>
                        <td className="px-4 py-3 text-right">
                          {d.status === 'pending' && (
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => reviewDeposit(d.id, 'approve')} data-testid={`dep-approve-${d.id}`}
                                className="p-1.5 rounded bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" title="Approve"><Check className="w-3.5 h-3.5" /></button>
                              <button onClick={() => reviewDeposit(d.id, 'reject')} data-testid={`dep-reject-${d.id}`}
                                className="p-1.5 rounded bg-red-500/15 text-red-400 hover:bg-red-500/25" title="Reject"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Withdrawals Tab */}
        {tab === 'withdrawals' && (
          <div className="space-y-4">
            <div className="flex gap-2">
              {['pending', 'approved', 'rejected', 'all'].map(f => (
                <button key={f} onClick={() => setWdFilter(f)} data-testid={`wd-filter-${f}`}
                  className={`px-3 py-1.5 rounded text-xs font-medium ${wdFilter === f ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] border border-[var(--border-subtle)]'}`}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded overflow-x-auto">
              {withdrawals.length === 0 ? (
                <div className="p-8 text-center text-sm text-[var(--text-secondary)]">No {wdFilter} withdrawals</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-[var(--text-secondary)] uppercase border-b border-[var(--border-subtle)]">
                      <th className="text-left px-4 py-3">User</th>
                      <th className="text-right px-3 py-3">Amount</th>
                      <th className="text-left px-3 py-3">Method</th>
                      <th className="text-left px-3 py-3">Account Details</th>
                      <th className="text-left px-3 py-3">Date</th>
                      <th className="text-left px-3 py-3">Status</th>
                      <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.map((w) => (
                      <tr key={w.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)]" data-testid={`wd-row-${w.id}`}>
                        <td className="px-4 py-3 align-top">
                          <p className="text-xs font-semibold text-[var(--text-primary)]">{w.user_name}</p>
                          <p className="text-[10px] text-[var(--text-tertiary)]">{w.user_email}</p>
                        </td>
                        <td className="px-3 py-3 text-right font-mono font-semibold text-red-500 align-top">{formatCurrency(w.amount)}</td>
                        <td className="px-3 py-3 text-xs text-[var(--text-secondary)] uppercase align-top">{w.method === 'bank_transfer' ? 'Bank' : w.method}</td>
                        <td className="px-3 py-3 text-xs text-[var(--text-primary)] align-top max-w-[260px]">
                          {w.bank_account ? (
                            <div className="space-y-0.5" data-testid={`wd-bank-${w.id}`}>
                              <p className="font-semibold text-[var(--text-primary)]">{w.bank_account.bank_name}</p>
                              <p className="text-[11px] text-[var(--text-secondary)]">{w.bank_account.account_holder} · <span className="capitalize">{w.bank_account.account_type}</span></p>
                              <p className="font-mono text-[11px] text-[var(--text-secondary)]">A/C: {w.bank_account.account_number}</p>
                              <p className="font-mono text-[11px] text-[var(--text-tertiary)]">IFSC: {w.bank_account.ifsc_code}</p>
                            </div>
                          ) : (
                            <span className="text-xs text-[var(--text-tertiary)] font-mono">{w.upi_id || w.bank_account_id || '-'}</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-xs text-[var(--text-secondary)] align-top">{new Date(w.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</td>
                        <td className="px-3 py-3 align-top"><span className={`px-2 py-0.5 rounded text-xs ${statusColor[w.status] || ''}`}>{w.status}</span></td>
                        <td className="px-4 py-3 text-right align-top">
                          {w.status === 'pending' && (
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => reviewWithdrawal(w.id, 'approve')} data-testid={`wd-approve-${w.id}`}
                                className="p-1.5 rounded bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25" title="Approve"><Check className="w-3.5 h-3.5" /></button>
                              <button onClick={() => reviewWithdrawal(w.id, 'reject')} data-testid={`wd-reject-${w.id}`}
                                className="p-1.5 rounded bg-red-500/15 text-red-500 hover:bg-red-500/25" title="Reject"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Screenshot Modal */}
        {screenshotUrl && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setScreenshotUrl(null)}>
            <div className="max-w-2xl max-h-[80vh] bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-subtle)]">
                <span className="text-sm font-semibold text-[var(--text-primary)]">Payment Screenshot</span>
                <button onClick={() => setScreenshotUrl(null)}><X className="w-5 h-5 text-[var(--text-secondary)]" /></button>
              </div>
              <img src={screenshotUrl} alt="Payment screenshot" className="w-full max-h-[70vh] object-contain" />
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
